// src/components/Forms/CourseOrderForm/CourseOrderForm.jsx
/* eslint-disable no-empty */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { IMaskInput } from "react-imask";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import {
  fetchOffer,
  validateCoupon,
  createGuestIntent,
  confirmGuestCheckout,
  createAuthIntent,
  confirmAuthCheckout,
  chargeSavedCard,
} from "../../../utils/coursesClient";

import OfferLineItems from "./OfferLineItems";
import BumpOffer from "./BumpOffer";
import InlineLogin from "./InlineLogin";
import CryptoJS from "crypto-js";
import "./CourseOrderForm.scss";

function encryptAuthData(token, callbackUrl) {
  try {
    const key =
      import.meta.env.VITE_ENCRYPTION_KEY ||
      "unclekam-default-encryption-key-2024";
    const payload = JSON.stringify({ token, callback_url: callbackUrl });
    return CryptoJS.AES.encrypt(payload, key).toString();
  } catch (e) {
    console.warn("SSO encryption failed:", e);
    return null;
  }
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeSlashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

const DEFAULT_TEST_KEY =
  "pk_test_51OQF1nH7lUdFHMOM1c6k74DSBk2KE8kaifr9tTGEEIs0skJWJ6wgIypEJqcKghyQkFoekUd33KIEH9OQg5MCr4KE00ok1marSi";

const stripeInputStyle = {
  style: {
    base: {
      fontSize: "14px",
      color: "#212529",
      "::placeholder": { color: "#ADB5BD" },
      fontFamily:
        'Open Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    invalid: { color: "#dc3545" },
  },
};

function getFriendlyErrorMessage(rawMessage) {
  if (!rawMessage) return "Payment failed. Please try again.";
  if (/card.*declined|insufficient_funds/i.test(rawMessage))
    return "Your card was declined. Please try a different card.";
  if (/expired_card/i.test(rawMessage))
    return "Your card has expired. Please use a valid card.";
  if (/incorrect_cvc/i.test(rawMessage))
    return "Incorrect CVC code. Please check your card details.";
  if (/processing_error/i.test(rawMessage))
    return "An error occurred while processing your card. Please try again.";
  return rawMessage;
}

function getEffectivePrice(offer) {
  if (offer?.price_type === "free") return 0;
  const raw = offer?.discounted_price ?? offer?.amount ?? offer?.price ?? "0";
  return Number.parseFloat(String(raw).trim()) || 0;
}

function parseBooleanProp(val, defaultVal = false) {
  if (val === undefined || val === null || val === "") return defaultVal;
  if (typeof val === "boolean") return val;
  if (typeof val === "string") {
    const s = val.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "on") return true;
    if (s === "false" || s === "0" || s === "off") return false;
  }
  return Boolean(val);
}

function CourseOrderFormInner(props) {
  const offersProp = props.offersProp ?? props.offers ?? "";
  const bumpOffersProp = props.bumpOffersProp ?? props["bump-offers"] ?? props.bump_offers ?? props.bumpOffers ?? "";
  const defaultOfferProp = props.defaultOfferProp ?? props["default-offer"] ?? props.default_offer ?? props.defaultOffer ?? "";
  const rawSelectMode = String(props.selectMode ?? props["select-mode"] ?? props.select_mode ?? "single").trim().toLowerCase();
  const isMultiMode = rawSelectMode === "multi" || rawSelectMode === "multiple";
  const showProducts = parseBooleanProp(props.showProducts ?? props["show-products"] ?? props.show_products, true);
  const showCoupon = parseBooleanProp(props.showCoupon ?? props["show-coupon"] ?? props.show_coupon, false);
  const redirectUrl = props.redirectUrl ?? props["redirect-url"] ?? props.redirect_url;
  const qa_mode = Boolean(props.qa_mode ?? props.qaMode ?? false);
  const proxyUrl = props.proxyUrl || null;
  const [step, setStep] = useState(1);

  // Step 1 Buyer details
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Auth state if buyer already exists or logs in
  const [authenticatedToken, setAuthenticatedToken] = useState(null);
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [showInlineLogin, setShowInlineLogin] = useState(false);

  // Loaded offers
  const [offers, setOffers] = useState([]);
  const [bumpOffers, setBumpOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(true);

  // Selection
  const [selectedOfferIds, setSelectedOfferIds] = useState(new Set());
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [selectedPricingType, setSelectedPricingType] = useState("one_time");
  const [selectedBumpIds, setSelectedBumpIds] = useState(new Set());

  // Coupon
  const [showCouponInput, setShowCouponInput] = useState(showCoupon);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [discountedAmount, setDiscountedAmount] = useState(null);
  const [couponStatus, setCouponStatus] = useState(null);
  const [couponMessage, setCouponMessage] = useState("");
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Status & Stripe Elements
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const [cardNumberReady, setCardNumberReady] = useState(false);
  const [cardExpiryReady, setCardExpiryReady] = useState(false);
  const [cardCvcReady, setCardCvcReady] = useState(false);
  const cardInputsReady = cardNumberReady && cardExpiryReady && cardCvcReady;

  const stripe = useStripe();
  const elements = useElements();

  // Load Primary Offers and Bump Offers from Courses Backend
  useEffect(() => {
    let cancelled = false;
    const primaryRefs = (Array.isArray(offersProp) ? offersProp : String(offersProp || "").split(","))
      .map((s) => s.trim())
      .filter(Boolean);

    const bumpRefs = (Array.isArray(bumpOffersProp) ? bumpOffersProp : String(bumpOffersProp || "").split(","))
      .map((s) => s.trim())
      .filter(Boolean);

    if (!primaryRefs.length) {
      setLoadingOffers(false);
      return;
    }

    setLoadingOffers(true);

    (async () => {
      try {
        const loadedPrimary = [];
        for (const ref of primaryRefs) {
          try {
            const data = await fetchOffer(ref, { qa_mode, proxyUrl });
            if (data?.id) loadedPrimary.push(data);
          } catch (e) {
            console.error(`Failed to load offer: ${ref}`, e);
          }
        }

        const loadedBumps = [];
        for (const ref of bumpRefs) {
          try {
            const data = await fetchOffer(ref, { qa_mode, proxyUrl });
            if (data?.id) loadedBumps.push(data);
          } catch (e) {
            console.error(`Failed to load bump offer: ${ref}`, e);
          }
        }

        if (cancelled) return;

        setOffers(loadedPrimary);
        setBumpOffers(loadedBumps);

        if (loadedPrimary.length > 0) {
          if (isMultiMode) {
            if (defaultOfferProp) {
              const matchDefault = loadedPrimary.find(
                (o) => o.embed_code === defaultOfferProp || o.checkout_slug === defaultOfferProp || o.id === defaultOfferProp
              );
              setSelectedOfferIds(new Set(matchDefault ? [matchDefault.id] : [loadedPrimary[0].id]));
            } else {
              setSelectedOfferIds(new Set([loadedPrimary[0].id]));
            }
          } else {
            const matchDefault = loadedPrimary.find(
              (o) => o.embed_code === defaultOfferProp || o.checkout_slug === defaultOfferProp || o.id === defaultOfferProp
            );
            const initial = matchDefault || loadedPrimary[0];
            setSelectedOfferId(initial.id);

            if (initial.payment_mode === "payment_plan") {
              setSelectedPricingType("payment_plan");
            } else {
              setSelectedPricingType("one_time");
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          setAlert({ type: "error", message: "Failed to load course offers." });
        }
      } finally {
        if (!cancelled) setLoadingOffers(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [offersProp, bumpOffersProp, defaultOfferProp, isMultiMode, qa_mode, proxyUrl]);

  // Selected primary offer object(s)
  const currentOffer = useMemo(
    () => offers.find((o) => o.id === selectedOfferId) || null,
    [offers, selectedOfferId]
  );

  const selectedPrimaryOffers = useMemo(() => {
    if (isMultiMode) {
      return offers.filter((o) => selectedOfferIds.has(o.id));
    }
    return currentOffer ? [currentOffer] : [];
  }, [isMultiMode, offers, selectedOfferIds, currentOffer]);

  const effectivePrimaryOffer = useMemo(() => {
    if (isMultiMode) {
      return selectedPrimaryOffers[0] || null;
    }
    return currentOffer;
  }, [isMultiMode, selectedPrimaryOffers, currentOffer]);

  // Total price calculation
  const totalPrice = useMemo(() => {
    let sum = 0;
    if (isMultiMode) {
      for (const offer of offers) {
        if (selectedOfferIds.has(offer.id)) {
          sum += getEffectivePrice(offer);
        }
      }
      if (discountedAmount !== null && effectivePrimaryOffer) {
        const diff = getEffectivePrice(effectivePrimaryOffer) - discountedAmount;
        if (diff > 0) sum = Math.max(0, sum - diff);
      }
    } else if (currentOffer) {
      if (currentOffer.price_type === "free") {
        sum += 0;
      } else if (selectedPricingType === "payment_plan") {
        const planRaw = currentOffer.payment_plan_amount ?? currentOffer.payment_plan_price ?? "0";
        sum += Number.parseFloat(String(planRaw).trim()) || 0;
      } else {
        const base = getEffectivePrice(currentOffer);
        sum += discountedAmount !== null ? discountedAmount : base;
      }
    }

    // Add selected bump offers
    for (const bump of bumpOffers) {
      if (selectedBumpIds.has(bump.id)) {
        sum += getEffectivePrice(bump);
      }
    }

    return Math.max(0, sum);
  }, [isMultiMode, offers, selectedOfferIds, currentOffer, effectivePrimaryOffer, selectedPricingType, discountedAmount, bumpOffers, selectedBumpIds]);

  const isFreeTotal = totalPrice === 0;

  // Option selection handler for single mode
  const handleSelectOption = (offerId, pricingType) => {
    setSelectedOfferId(offerId);
    if (pricingType !== selectedPricingType) {
      setSelectedPricingType(pricingType);
      // Reset coupon on pricing type switch to prevent mismatch
      setDiscountedAmount(null);
      setAppliedCoupon("");
      setCouponStatus(null);
      setCouponMessage("");
    }
  };

  // Toggle handler for multi mode
  const handleToggleOffer = (offerId) => {
    setSelectedOfferIds((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) {
        next.delete(offerId);
      } else {
        next.add(offerId);
      }
      return next;
    });
  };

  // Bump toggle handler
  const handleToggleBump = (bumpId) => {
    setSelectedBumpIds((prev) => {
      const next = new Set(prev);
      next.has(bumpId) ? next.delete(bumpId) : next.add(bumpId);
      return next;
    });
  };

  // Coupon validation
  const handleApplyCoupon = async (e) => {
    e?.preventDefault();
    if (!couponCode.trim() || !effectivePrimaryOffer) return;
    setValidatingCoupon(true);
    setCouponMessage("");

    try {
      const res = await validateCoupon(
        { code: couponCode.trim(), offerId: effectivePrimaryOffer.id },
        { qa_mode, proxyUrl }
      );
      const { discounted_amount, message } = res || {};
      setDiscountedAmount(discounted_amount);
      setAppliedCoupon(couponCode.trim());
      setCouponStatus("valid");
      setCouponMessage(message || "Coupon applied successfully!");
    } catch (err) {
      setCouponStatus("invalid");
      setDiscountedAmount(null);
      setAppliedCoupon("");
      setCouponMessage(
        err.response?.data?.error?.message ||
          err.response?.data?.message ||
          "Invalid coupon code."
      );
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Step 1 Validation
  const handleGoStep2 = (e) => {
    e.preventDefault();
    setAlert({ type: "", message: "" });

    const cleanEmail = email.trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail);
    const rawPhone = (phone || "").trim();
    const digits = rawPhone.replace(/\D/g, "");
    const isInternational = rawPhone.startsWith("+");

    if (!firstName.trim() || !lastName.trim()) {
      setAlert({ type: "error", message: "Please provide both first and last name." });
      return;
    }
    if (!emailOk) {
      setAlert({ type: "error", message: "Please enter a valid email address." });
      return;
    }
    if (isInternational) {
      if (digits.length < 8 || digits.length > 15) {
        setAlert({ type: "error", message: "Please enter a valid international phone number." });
        return;
      }
    } else {
      if (digits.length !== 10) {
        setAlert({ type: "error", message: "Please enter a complete 10-digit phone number: (000) 000-0000." });
        return;
      }
    }
    if (!authenticatedToken) {
      if (password.length < 8) {
        setAlert({
          type: "error",
          message: "Password must be at least 8 characters long.",
        });
        return;
      }
      if (password !== confirmPassword) {
        setAlert({ type: "error", message: "Passwords do not match." });
        return;
      }
    }

    setStep(2);
  };

  // Login callback from InlineLogin
  const handleLoginSuccess = (token, user) => {
    setAuthenticatedToken(token);
    setAuthenticatedUser(user);
    setShowInlineLogin(false);
    if (user?.firstname) setFirstName(user.firstname);
    if (user?.lastname) setLastName(user.lastname);
    setAlert({
      type: "success",
      message: `Logged in as ${user.email}. You can now proceed to Step #2.`,
    });
  };

  // Final Order Submission
  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setAlert({ type: "", message: "" });

    if (!selectedPrimaryOffers.length || !effectivePrimaryOffer) {
      setAlert({ type: "error", message: "Please select at least one offer to continue." });
      return;
    }

    setSubmitting(true);

    try {
      let activeToken = authenticatedToken;
      const cleanEmail = email.trim();
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const rawPhone = (phone || "").trim();
      const cleanPhone = rawPhone.replace(/\D/g, "");

      // ──────────────────────────────────────────────────────────────────────
      // 1. FREE OFFER PATH
      // ──────────────────────────────────────────────────────────────────────
      if (isFreeTotal) {
        if (!activeToken) {
          const guestRes = await createGuestIntent(
            {
              offer_id: effectivePrimaryOffer.id,
              email: cleanEmail,
              firstname: firstName.trim(),
              lastname: lastName.trim(),
              phone: cleanPhone,
              password,
              coupon_code: appliedCoupon || undefined,
            },
            { qa_mode, proxyUrl }
          );
          activeToken = guestRes?.token || guestRes?.data?.token;
        } else {
          await createAuthIntent(
            {
              offer_id: effectivePrimaryOffer.id,
              coupon_code: appliedCoupon || undefined,
            },
            activeToken,
            { qa_mode, proxyUrl }
          );
        }

        // Fulfill any additional free primary offers in multi mode
        if (isMultiMode && selectedPrimaryOffers.length > 1 && activeToken) {
          for (const extraOffer of selectedPrimaryOffers.slice(1)) {
            try {
              await createAuthIntent({ offer_id: extraOffer.id }, activeToken, { qa_mode, proxyUrl });
            } catch (extraErr) {
              console.warn(`Additional free offer ${extraOffer.id} enrollment failed:`, extraErr);
            }
          }
        }

        setAlert({ type: "success", message: "Enrollment successful!" });
        finishRedirect(cleanEmail, activeToken);
        return;
      }

      // Paid offers require Stripe
      if (!stripe || !elements) {
        setAlert({ type: "error", message: "Payment processor is not ready yet." });
        setSubmitting(false);
        return;
      }

      const cardElement = elements.getElement(CardNumberElement);

      // ──────────────────────────────────────────────────────────────────────
      // 2. PAYMENT PLAN SETUP FLOW
      // ──────────────────────────────────────────────────────────────────────
      if (selectedPricingType === "payment_plan") {
        let setupIntentSecret = null;

        if (!activeToken) {
          const guestRes = await createGuestIntent(
            {
              offer_id: effectivePrimaryOffer.id,
              email: cleanEmail,
              firstname: firstName.trim(),
              lastname: lastName.trim(),
              phone: cleanPhone,
              password,
              pricing_type: "payment_plan",
              coupon_code: appliedCoupon || undefined,
            },
            { qa_mode, proxyUrl }
          );

          setupIntentSecret =
            guestRes?.setup_intent_client_secret ||
            guestRes?.data?.setup_intent_client_secret;
          activeToken = guestRes?.token || guestRes?.data?.token;
          if (activeToken) setAuthenticatedToken(activeToken);
        } else {
          const authRes = await createAuthIntent(
            {
              offer_id: effectivePrimaryOffer.id,
              pricing_type: "payment_plan",
              coupon_code: appliedCoupon || undefined,
            },
            activeToken,
            { qa_mode, proxyUrl }
          );
          setupIntentSecret =
            authRes?.setup_intent_client_secret ||
            authRes?.data?.setup_intent_client_secret;
        }

        if (!setupIntentSecret) {
          throw new Error("Unable to initiate payment plan setup.");
        }

        const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
          setupIntentSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: { name: fullName, email: cleanEmail, phone: cleanPhone },
            },
          }
        );

        if (setupError) {
          setAlert({
            type: "error",
            message: getFriendlyErrorMessage(setupError.message),
          });
          setSubmitting(false);
          return;
        }

        if (setupIntent.status !== "succeeded") {
          setAlert({
            type: "error",
            message: `Card setup status: ${setupIntent.status}. Please try again.`,
          });
          setSubmitting(false);
          return;
        }

        // Charge saved card to activate subscription
        const chargeRes = await chargeSavedCard(
          {
            offer_id: effectivePrimaryOffer.id,
            payment_method_id: setupIntent.payment_method,
            pricing_type: "payment_plan",
          },
          activeToken,
          { qa_mode, proxyUrl }
        );

        const subData = chargeRes?.data || chargeRes;
        if (subData?.status === "requires_action" && subData?.client_secret) {
          const { error: scaError, paymentIntent } = await stripe.confirmCardPayment(
            subData.client_secret
          );
          if (scaError) {
            setAlert({
              type: "error",
              message: getFriendlyErrorMessage(scaError.message),
            });
            setSubmitting(false);
            return;
          }
          if (paymentIntent.status !== "succeeded") {
            setAlert({
              type: "error",
              message: `Payment status: ${paymentIntent.status}.`,
            });
            setSubmitting(false);
            return;
          }
          await confirmAuthCheckout(
            {
              purchase_id: subData.purchase_id,
              payment_intent_id: paymentIntent.id,
            },
            activeToken,
            { qa_mode, proxyUrl }
          );
        }

        // Fulfil any additional primary offers in multi mode
        await processAdditionalPrimaryOffers(activeToken, cardElement, fullName, cleanEmail, cleanPhone);

        // Fulfil optional bump offer if selected
        await processSelectedBumpOffers(activeToken, cardElement, fullName, cleanEmail, cleanPhone);

        setAlert({ type: "success", message: "Payment Plan Activated Successfully!" });
        finishRedirect(cleanEmail, activeToken);
        return;
      }

      // ──────────────────────────────────────────────────────────────────────
      // 3. ONE-TIME PAYMENT FLOW
      // ──────────────────────────────────────────────────────────────────────
      let clientSecret = null;
      let purchaseId = null;

      if (!activeToken) {
        const guestRes = await createGuestIntent(
          {
            offer_id: effectivePrimaryOffer.id,
            email: cleanEmail,
            firstname: firstName.trim(),
            lastname: lastName.trim(),
            phone: cleanPhone,
            password,
            pricing_type: "one_time",
            coupon_code: appliedCoupon || undefined,
          },
          { qa_mode, proxyUrl }
        );

        const d = guestRes?.data || guestRes;
        clientSecret = d?.client_secret;
        purchaseId = d?.purchase_id;
        activeToken = d?.token;
        if (activeToken) setAuthenticatedToken(activeToken);
      } else {
        const authRes = await createAuthIntent(
          {
            offer_id: effectivePrimaryOffer.id,
            pricing_type: "one_time",
            coupon_code: appliedCoupon || undefined,
          },
          activeToken,
          { qa_mode, proxyUrl }
        );
        const d = authRes?.data || authRes;
        clientSecret = d?.client_secret;
        purchaseId = d?.purchase_id;
      }

      if (!clientSecret) {
        throw new Error("Payment initialization failed. Please try again.");
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: { name: fullName, email: cleanEmail, phone: cleanPhone },
          },
        }
      );

      if (stripeError) {
        setAlert({
          type: "error",
          message: getFriendlyErrorMessage(stripeError.message),
        });
        setSubmitting(false);
        return;
      }

      if (paymentIntent.status !== "succeeded") {
        setAlert({
          type: "error",
          message: `Payment status: ${paymentIntent.status}.`,
        });
        setSubmitting(false);
        return;
      }

      // Confirm purchase in courses backend to grant enrollments and ebooks
      if (purchaseId) {
        await confirmGuestCheckout(
          {
            purchase_id: purchaseId,
            payment_intent_id: paymentIntent.id,
          },
          { qa_mode, proxyUrl }
        );
      }

      // Fulfil any additional primary offers in multi mode
      await processAdditionalPrimaryOffers(activeToken, cardElement, fullName, cleanEmail, cleanPhone);

      // Fulfil optional bump offer if selected
      await processSelectedBumpOffers(activeToken, cardElement, fullName, cleanEmail, cleanPhone);

      setAlert({ type: "success", message: "Order Completed Successfully!" });
      finishRedirect(cleanEmail, activeToken);
    } catch (err) {
      console.error("Order completion error:", err);
      const msg =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        "An error occurred while processing your order.";

      // Edge case: Account already exists
      if (/already exists/i.test(msg)) {
        setShowInlineLogin(true);
        setStep(1);
        setAlert({
          type: "error",
          message: "An account already exists for this email. Please enter your password to log in.",
        });
      } else {
        setAlert({ type: "error", message: getFriendlyErrorMessage(msg) });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Sequential fulfillment for additional primary offers in multi-select mode
  const processAdditionalPrimaryOffers = async (token, cardElement, fullName, email, phone) => {
    if (!token || !isMultiMode || selectedPrimaryOffers.length <= 1) return;
    const additional = selectedPrimaryOffers.slice(1);
    for (const addOffer of additional) {
      try {
        if (addOffer.price_type === "free") {
          await createAuthIntent({ offer_id: addOffer.id }, token, { qa_mode, proxyUrl });
        } else {
          const intentRes = await createAuthIntent(
            { offer_id: addOffer.id, pricing_type: "one_time" },
            token,
            { qa_mode, proxyUrl }
          );
          const d = intentRes?.data || intentRes;
          if (d?.client_secret) {
            const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(
              d.client_secret,
              {
                payment_method: {
                  card: cardElement,
                  billing_details: { name: fullName, email, phone },
                },
              }
            );
            if (!stripeErr && paymentIntent?.status === "succeeded") {
              await confirmAuthCheckout(
                { purchase_id: d.purchase_id, payment_intent_id: paymentIntent.id },
                token,
                { qa_mode, proxyUrl }
              );
            }
          }
        }
      } catch (addErr) {
        console.warn(`Additional primary offer ${addOffer.id} fulfillment failed:`, addErr);
      }
    }
  };

  // Sequential bump offer fulfillment using active buyer token
  const processSelectedBumpOffers = async (token, cardElement, fullName, email, phone) => {
    if (!token || !selectedBumpIds.size) return;

    for (const bumpId of selectedBumpIds) {
      try {
        const intentRes = await createAuthIntent(
          { offer_id: bumpId, pricing_type: "one_time" },
          token,
          { qa_mode, proxyUrl }
        );
        const d = intentRes?.data || intentRes;
        if (d?.client_secret) {
          const { error: bumpStripeErr, paymentIntent } = await stripe.confirmCardPayment(
            d.client_secret,
            {
              payment_method: {
                card: cardElement,
                billing_details: { name: fullName, email, phone },
              },
            }
          );
          if (!bumpStripeErr && paymentIntent?.status === "succeeded") {
            await confirmAuthCheckout(
              { purchase_id: d.purchase_id, payment_intent_id: paymentIntent.id },
              token,
              { qa_mode, proxyUrl }
            );
          }
        }
      } catch (bumpErr) {
        console.warn(`Bump offer ${bumpId} charge failed:`, bumpErr);
      }
    }
  };

  // Redirect on success
  const finishRedirect = (buyerEmail, buyerToken) => {
    setTimeout(() => {
      if (redirectUrl) {
        try {
          const url = new URL(redirectUrl.trim(), window.location.origin);
          if (buyerEmail) url.searchParams.set("email", buyerEmail);
          window.location.href = url.toString();
        } catch {
          window.location.href = redirectUrl.trim();
        }
        return;
      }

      const isLocalhost =
        typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1");

      const coursesBase = isLocalhost
        ? "http://localhost:5181"
        : qa_mode
        ? (import.meta.env.VITE_COURSES_APP_STAGING_URL || "https://unclekam-courses-app-staging.up.railway.app")
        : (import.meta.env.VITE_COURSES_APP_PROD_URL || "https://courses.unclekam.com");

      // Direct to /ebooks if offer only has ebooks, otherwise /my-courses
      const isEbookOnly =
        currentOffer?.connected_products?.length > 0 &&
        currentOffer.connected_products.every((p) => p.type === "ebook");
      const targetPath = isEbookOnly ? "/ebooks" : "/my-courses";

      const activeJwt = buyerToken || authenticatedToken;
      if (activeJwt) {
        const encrypted = encryptAuthData(activeJwt, targetPath);
        if (encrypted) {
          window.location.href = `${coursesBase.replace(/\/+$/, "")}/auth?data=${encodeURIComponent(encrypted)}`;
          return;
        }
      }

      window.location.href = `${coursesBase.replace(/\/+$/, "")}${targetPath}`;
    }, 1200);
  };

  if (loadingOffers) {
    return (
      <div className="container text-center py-5" style={{ maxWidth: 600 }}>
        <div className="osof-loader mb-3" style={{ borderColor: "#3d5fff", borderBottomColor: "transparent" }} />
        <p className="text-muted fw-semibold">Loading course details...</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: 600 }}>
      {/* Step Indicator Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-center w-50" style={{ cursor: "pointer" }} onClick={() => setStep(1)}>
          <p
            className={`mb-0 fw-bold text-center ${step === 1 ? "text-primary" : "text-muted"}`}
            style={{ color: step === 1 ? "#3d5fff" : undefined }}
          >
            Create Account
          </p>
          <small className="text-muted text-center">Contact Information</small>
        </div>
        <div
          className="text-center w-50"
          style={{ cursor: step === 2 ? "pointer" : "default" }}
          onClick={() => step === 2 && setStep(2)}
        >
          <p
            className={`mb-0 fw-bold text-center ${step === 2 ? "text-primary" : "text-muted"}`}
            style={{ color: step === 2 ? "#3d5fff" : undefined }}
          >
            Your Information
          </p>
          <small className="text-muted">Your Billing Information</small>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div
          style={{
            height: 3,
            width: step === 1 ? "50%" : "100%",
            background: "#3d5fff",
            borderRadius: 2,
            transition: "width .25s ease",
          }}
        />
      </div>

      {/* Global Alerts */}
      {alert.message && (
        <div
          className={`alert ${alert.type === "error" ? "alert-danger" : "alert-success"} mb-3`}
          role="alert"
        >
          {alert.message}
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* STEP 1: CONTACT & ACCOUNT CREATION                                   */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="bg-transparent rounded-3 p-0 mt-2">
          {showInlineLogin ? (
            <InlineLogin
              email={email}
              onLoginSuccess={handleLoginSuccess}
              onCancel={() => setShowInlineLogin(false)}
              qa_mode={qa_mode}
              proxyUrl={proxyUrl}
            />
          ) : (
            <form onSubmit={handleGoStep2}>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <input
                    type="text"
                    required
                    placeholder="First Name"
                    className="form-control osof-input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                  />
                </div>
                <div className="col-6">
                  <input
                    type="text"
                    required
                    placeholder="Last Name"
                    className="form-control osof-input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div className="mb-3">
                <input
                  type="email"
                  required
                  placeholder="Email Address (Login / Account Access)"
                  className="form-control osof-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>

              <div className="mb-3">
                <IMaskInput
                  name="phone"
                  type="tel"
                  required
                  placeholder="Phone Number (000) 000-0000"
                  className="form-control osof-input"
                  mask={[
                    {
                      mask: "(000) 000-0000",
                      country: "US",
                    },
                    {
                      mask: "+1 (000) 000-0000",
                      country: "US_INTL",
                    },
                    {
                      mask: "+995 (000) 000-000",
                      country: "GE",
                    },
                    {
                      mask: "+00000000000000",
                      country: "INTL",
                    },
                  ]}
                  dispatch={(appended, dynamicMasked) => {
                    const raw = dynamicMasked.value + appended;
                    if (raw.startsWith("+1")) {
                      return dynamicMasked.compiledMasks.find((m) => m.country === "US_INTL");
                    }
                    if (raw.startsWith("+995")) {
                      return dynamicMasked.compiledMasks.find((m) => m.country === "GE");
                    }
                    if (raw.startsWith("+")) {
                      return dynamicMasked.compiledMasks.find((m) => m.country === "INTL");
                    }
                    return dynamicMasked.compiledMasks.find((m) => m.country === "US");
                  }}
                  lazy={true}
                  value={phone}
                  onAccept={(val) => setPhone(val)}
                  autoComplete="tel"
                />
              </div>

              {!authenticatedToken ? (
                <>
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <div className="osof-password-wrapper">
                        <input
                          type={showPassword ? "text" : "password"}
                          required
                          placeholder="Create Password (8+ chars)"
                          className="form-control osof-input"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          minLength={8}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="osof-password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                        </button>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="osof-password-wrapper">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          required
                          placeholder="Confirm Password"
                          className="form-control osof-input"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          minLength={8}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="osof-password-toggle"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                        >
                          {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="text-end mb-3">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-decoration-none p-0 text-primary fw-semibold"
                      onClick={() => setShowInlineLogin(true)}
                    >
                      Already have an account? Log in
                    </button>
                  </div>
                </>
              ) : (
                <div className="alert alert-info py-2 px-3 small mb-3">
                  ✓ Logged in as <strong>{authenticatedUser?.email || email}</strong>
                </div>
              )}

              <div className="d-grid mt-2">
                <button
                  type="submit"
                  className="btn fw-bold btn-primary py-3 fs-5 osof-btn"
                >
                  Go To Step #2
                </button>
              </div>

              <p className="text-center mt-2 text-muted" style={{ fontSize: 12 }}>
                * We Respect Your Privacy &amp; Information *
              </p>
            </form>
          )}
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* STEP 2: BILLING & PAYMENT INFORMATION                                */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      {step === 2 && (
        <form className="bg-transparent rounded-3 p-0 mt-3" onSubmit={handleSubmitOrder}>
          {/* Offer Line Items Table */}
          <OfferLineItems
            offers={offers}
            isMultiMode={isMultiMode}
            selectedOfferIds={selectedOfferIds}
            selectedOfferId={selectedOfferId}
            selectedPricingType={selectedPricingType}
            onSelectOption={handleSelectOption}
            onToggleOffer={handleToggleOffer}
            showProducts={showProducts}
            appliedCoupon={appliedCoupon}
            discountedAmount={discountedAmount}
            totalPrice={totalPrice}
          />

          {/* Bump Offer Box (if configured) */}
          {bumpOffers.map((bump) => (
            <BumpOffer
              key={bump.id}
              bumpOffer={bump}
              isSelected={selectedBumpIds.has(bump.id)}
              onToggle={handleToggleBump}
            />
          ))}

          {/* Coupon Code Section */}
          <div className="mt-3">
            {!showCouponInput ? (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 osof-coupon-link"
                onClick={() => setShowCouponInput(true)}
              >
                + Have a promo or coupon code?
              </button>
            ) : (
              <div className="p-2 border rounded bg-white mt-1">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control osof-input"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    disabled={validatingCoupon}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary fw-semibold"
                    onClick={handleApplyCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                  >
                    {validatingCoupon ? "Checking..." : "Apply"}
                  </button>
                </div>
                {couponMessage && (
                  <div
                    className={`small mt-1 ${
                      couponStatus === "valid" ? "text-success" : "text-danger"
                    }`}
                  >
                    {couponMessage}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stripe Card Elements (only if paid offer) */}
          {!isFreeTotal && (
            <div className="row g-2 mt-3">
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold small mb-1">Card Number</label>
                <div className="osof-stripe">
                  <CardNumberElement
                    options={stripeInputStyle}
                    onChange={(e) => setCardNumberReady(e.complete)}
                  />
                </div>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label fw-semibold small mb-1">Expiration</label>
                <div className="osof-stripe">
                  <CardExpiryElement
                    options={stripeInputStyle}
                    onChange={(e) => setCardExpiryReady(e.complete)}
                  />
                </div>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label fw-semibold small mb-1">CVC</label>
                <div className="osof-stripe">
                  <CardCvcElement
                    options={stripeInputStyle}
                    onChange={(e) => setCardCvcReady(e.complete)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-4">
            <button
              type="submit"
              className="btn btn-primary text-white w-100 d-flex align-items-center justify-content-center fw-bold fs-5 py-3 osof-btn"
              disabled={submitting || (!isFreeTotal && !cardInputsReady)}
            >
              {submitting ? (
                <span className="osof-loader" />
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    fill="white"
                    className="me-2"
                  >
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                  </svg>
                  {isFreeTotal ? "Complete Free Enrollment" : "Complete Order"}
                </>
              )}
            </button>
          </div>

          <div className="text-center mt-2">
            <button
              type="button"
              className="btn btn-link btn-sm text-muted text-decoration-none"
              onClick={() => setStep(1)}
            >
              ← Back to Contact Information
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function CourseOrderForm(props = {}) {
  const offers = props.offers ?? props.offersProp ?? "";
  const bumpOffers = props["bump-offers"] ?? props.bump_offers ?? props.bumpOffers ?? props.bumpOffersProp ?? "";
  const defaultOffer = props["default-offer"] ?? props.default_offer ?? props.defaultOffer ?? props.defaultOfferProp ?? "";
  const selectMode = props["select-mode"] ?? props.select_mode ?? props.selectMode ?? "single";
  const showProducts = parseBooleanProp(props["show-products"] ?? props.show_products ?? props.showProducts ?? props.showProductsProp, true);
  const showCoupon = parseBooleanProp(props["show-coupon"] ?? props.show_coupon ?? props.showCoupon ?? props.showCouponProp, false);
  const redirectUrl = props["redirect-url"] ?? props.redirect_url ?? props.redirectUrl ?? "";
  const testing = props.testing ?? false;
  const qa_mode = props.qa_mode ?? props.qaMode ?? false;
  const testKey = props.testKey || import.meta.env.VITE_STRIPE_STAGING_KEY || DEFAULT_TEST_KEY;
  const liveKey = props.liveKey || import.meta.env.VITE_STRIPE_PROD_KEY;
  const proxyUrl = props.proxyUrl || null;

  const qaModeURL =
    typeof window !== "undefined" &&
    (new URLSearchParams(window.location.search).get("qa_mode") === "on" ||
      new URLSearchParams(window.location.search).get("qa_mode") === "true" ||
      new URLSearchParams(window.location.search).get("testing") === "true");

  const useTest = Boolean(testing || qa_mode || qaModeURL);
  const activeKey = useTest ? testKey : (liveKey || testKey);
  const stripePromise = useMemo(
    () => loadStripe(activeKey),
    [activeKey]
  );

  return (
    <Elements key={activeKey} stripe={stripePromise}>
      <CourseOrderFormInner
        offersProp={offers}
        bumpOffersProp={bumpOffers}
        defaultOfferProp={defaultOffer}
        selectMode={selectMode}
        showProducts={showProducts}
        showCoupon={showCoupon}
        redirectUrl={redirectUrl}
        qa_mode={useTest}
        proxyUrl={proxyUrl}
      />
    </Elements>
  );
}
